import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  Alert,
  InputAdornment,
  TextField,
  Modal,
  Dialog,
  IconButton,
  Stack,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TablePagination,
} from "@mui/material";
import { CirclePlus, Search } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import {
  getOrders,
  createOrder,
  updateOrder,
  deleteOrder,
} from "../../api/Order/api";
import usePermissions from "./hooks/usePermissions";
import DataTable from "../../components/DataTable";
import OrderForm from "../../components/shared/forms/OrderForm";
import { AuthContext } from "../../contexts/AuthContext";
import { formatDate } from "../../utils/dateUtils";
import { getDukas } from "../../api/Duka/api";
import { getProducts } from "../../api/Product/api";
import Toast from "../../components/shared/Toast";
import { showSuccessToast, showErrorToast } from "../../utils/toastUtils";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { SlidersHorizontal } from "lucide-react";
import { extractErrorMessages } from "../../utils/errorHandler";
import { getStocks } from "../../api/Stock Management/api";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const filterContainerStyle = {
  backgroundColor: "#ededed",
  padding: "20px",
  borderRadius: "4px",
  marginBottom: "20px",
};

const searchContainerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "20px",
};

const filterFieldStyle = {
  height: "40px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#14784A",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#14784A",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#666666",
    "&.Mui-focused": {
      color: "#14784A",
    },
  },
  "& .MuiInputAdornment-root": {
    "& .MuiSvgIcon-root": {
      color: "#666666",
    },
  },
  "& .MuiSelect-select.MuiSelect-outlined.MuiInputBase-input.MuiOutlinedInput-input":
    {
      color: "#666666",
    },
  "& .MuiMenuItem-root": {
    color: "#666666",
  },
};

const buttonBaseStyle = {
  height: "40px",
  minWidth: "6.042vw",
  textTransform: "none",
  fontWeight: 500,
};

const filterButtonStyle = {
  ...buttonBaseStyle,
  backgroundColor: "#124F3E",
  display: "flex",
  justifyContent: "space-between",
  "&:hover": {
    backgroundColor: "#1a614d !important",
  },
  fontWeight: "bold",
};

const clearButtonStyle = {
  ...buttonBaseStyle,
  fontWeight: "bold",
  bgcolor: "#e0e0e0",
  color: "#000",
  border: "none",
  "&:hover": {
    backgroundColor: "#d5d5d5 !important",
  },
};

const ORDER_STATUS = {
  PENDING: "PENDING",
  PROCESSING: "PROCESSING",
  SHIPPED: "SHIPPED",
  DELIVERED: "DELIVERED",
  CANCELLED: "CANCELLED",
};

const OrderList = () => {
  const { user } = useContext(AuthContext);
  const { can } = usePermissions();

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openForm, setOpenForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [dukas, setDukas] = useState([]);
  const [products, setProducts] = useState([]);
  const [stocks, setStocks] = useState([]);

  // Filter states
  const [tempFilters, setTempFilters] = useState({
    start_date: null,
    end_date: null,
    duka_name: "",
    status: "",
  });

  useEffect(() => {
    fetchOrders();
    fetchDukas();
  }, [page, rowsPerPage]);

  // Separate effect for search functionality
  useEffect(() => {
    if (orders.length > 0) {
      const filtered = orders.filter((order) =>
        Object.values(order).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
      setFilteredOrders(filtered);
    }
  }, [searchQuery, orders]);

  const fetchDukas = async () => {
    try {
      const [dukaResponse, productResponse, stockResponse] = await Promise.all([
        getDukas(),
        getProducts(),
        getStocks(),
      ]);
      setDukas(dukaResponse.data);
      setProducts(productResponse.data);
      setStocks(stockResponse.data);
    } catch (err) {
      showErrorToast("Failed to fetch data");
    }
  };

  const fetchOrders = async (filters = null) => {
    setLoading(true);

    // Prepare filters
    const queryParams = {
      page: page + 1,
      limit: rowsPerPage,
      ...(filters || {}),
    };

    try {
      const response = await getOrders(queryParams);
      setOrders(response.data);
      setFilteredOrders(response.data);
      setTotal(response.total);
      setError(null);
    } catch (err) {
      setError("Failed to fetch orders");
      showErrorToast("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const handleTempFilterChange = (field, value) => {
    setTempFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyFilters = () => {
    const cleanFilters = {};

    if (tempFilters.start_date) {
      cleanFilters.start_date = dayjs(tempFilters.start_date).format(
        "YYYY-MM-DD"
      );
    }
    if (tempFilters.end_date) {
      cleanFilters.end_date = dayjs(tempFilters.end_date).format("YYYY-MM-DD");
    }
    if (tempFilters.duka_name) {
      cleanFilters.duka_name = tempFilters.duka_name;
    }
    if (tempFilters.status) {
      cleanFilters.status = tempFilters.status;
    }

    fetchOrders(cleanFilters);
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      start_date: null,
      end_date: null,
      duka_name: "",
      status: "",
    };
    setTempFilters(clearedFilters);
    fetchOrders(); // Fetch without filters
  };

  const handleOpenForm = (order = null) => {
    setSelectedOrder(order);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setSelectedOrder(null);
    setOpenForm(false);
  };

  const handleOpenDeleteModal = (order) => {
    setSelectedOrder(order);
    setOpenDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setSelectedOrder(null);
    setOpenDeleteModal(false);
  };

  const handleSubmit = async (formData) => {
    try {
      if (selectedOrder) {
        await updateOrder(selectedOrder.id, formData);
        showSuccessToast("Order updated successfully");
      } else {
        await createOrder(formData);
        showSuccessToast("Order created successfully");
      }
      fetchOrders();
      handleCloseForm();
    } catch (err) {
      const messages = extractErrorMessages(err);
      messages.forEach(message => {
        showErrorToast(message);
      });
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (selectedOrder) {
        await deleteOrder(selectedOrder.id);
        showSuccessToast("Order deleted successfully");
        fetchOrders();
        handleCloseDeleteModal();
      }
    } catch (err) {
      showErrorToast(err.message);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const columns = [
    { id: "order_number", label: "Order Number", width: 150 },
    {
      id: "duka",
      label: "Duka",
      width: 150,
      format: (value) => {
        const duka = dukas.find((d) => d.id === value);
        return duka ? duka.name : value;
      },
    },
    {
      id: "product",
      label: "Product",
      width: 150,
      format: (value) => {
        const product = products.find((p) => p.id === value);
        return product ? product.name : value;
      },
    },
    { id: "order_type", label: "Type", width: 100 },
    { id: "status", label: "Status", width: 120 },
    {
      id: "order_date",
      label: "Order Date",
      width: 120,
      // format: (value) => formatDate(value)
    },
    {
      id: "selected_stock_info",
      label: "Stock",
      width: 180,
    },
    {
      id: "delivery_date",
      label: "Delivery Date",
      width: 120,
      // format: (value) => formatDate(value)
    },
    {
      id: "quantity",
      label: "Quantity",
      width: 120,
    },
    {
      id: "unit_price",
      label: "Unit Price (KES)",
      width: 120,
      format: (value) => (value ? `${value}` : ""),
    },
    {
      id: "total_price",
      label: "Total Amount (KES)",
      width: 120,
      format: (value) => (value ? `${value}` : ""),
    },
  ];

  if (!can.viewOrder()) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view orders.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Toast />
      {error && <Alert severity="error">{error}</Alert>}

      {/* Search and Add New button row */}
      <Box sx={searchContainerStyle}>
        <TextField
          placeholder="Search"
          size="small"
          value={searchQuery}
          sx={{
            width: "300px",
            ...filterFieldStyle,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {can.createOrder() && (
          <Button
            variant="contained"
            startIcon={<CirclePlus size={18} />}
            onClick={() => setOpenForm(true)}
            sx={{
              fontWeight: "bold",
              bgcolor: "#124F3E",
              "&:hover": {
                backgroundColor: "#1a614d !important",
              },
            }}
          >
            Add Order
          </Button>
        )}
      </Box>

      {/* Filter Controls */}
      <Box sx={filterContainerStyle}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="From Date"
                value={tempFilters.start_date}
                onChange={(date) => handleTempFilterChange("start_date", date)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    sx={filterFieldStyle}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="To Date"
                value={tempFilters.end_date}
                onChange={(date) => handleTempFilterChange("end_date", date)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    sx={filterFieldStyle}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <Autocomplete
              size="small"
              options={dukas}
              getOptionLabel={(option) => option.name || ""}
              value={
                dukas.find((d) => d.name === tempFilters.duka_name) || null
              }
              onChange={(_, newValue) =>
                handleTempFilterChange(
                  "duka_name",
                  newValue ? newValue.name : ""
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Duka"
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth size="small">
              <Select
                value={tempFilters.status}
                displayEmpty
                onChange={(e) =>
                  handleTempFilterChange("status", e.target.value)
                }
                notched
              >
                <MenuItem value="" sx={{ color: "#666666" }}>
                  Status
                </MenuItem>
                {Object.entries(ORDER_STATUS).map(([key, value]) => (
                  <MenuItem key={key} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={filterButtonStyle}
              >
                <SlidersHorizontal size={16} /> Filter
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={clearButtonStyle}
              >
                Clear
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <DataTable
        loading={loading}
        columns={columns}
        rows={filteredOrders}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        totalRows={total}
        onEdit={
          can.updateOrder()
            ? (rowData) => {
                setSelectedOrder(rowData);
                setOpenForm(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        editTooltip="Edit"
        components={{
          Pagination: () => (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          ),
        }}
      />

      <Modal open={openForm} onClose={handleCloseForm}>
        <Box sx={modalStyle}>
          <OrderForm
            onSubmit={handleSubmit}
            onCancel={handleCloseForm}
            initialData={selectedOrder}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default OrderList;
